import moment from "moment";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import AppointmentService from "../../api/AppointmentService";
import { TrackingEventService } from "../../api/TrackingEventService";
import UserService from "../../api/UserService";
import { useAppointment } from "../../hooks/useAppointment";
import { useAppointmentClicked } from "../../hooks/useAppointmentClicked";
import { RootState } from "../../store";
import { setAppointment } from "../../store/appointment-slice";
import { clockActions } from "../../store/clock-slice";
import { overviewAppointmentSliceActions } from "../../store/overview-appointment-slice";
import { ROLES, userSliceActions } from "../../store/user-slice";
import { calculateTotalTime } from "../../utils/calculateTotalTime";
import { AppointmentCard } from "../AppointmentCard/AppointmentCard";
import { LoadingSpinner } from "../LoadingSpinner/LoadingSpinner";
import { NoData } from "../NoData/NoData";
import { SubMenu } from "../SubMenu/SubMenu";
import { Swipeable } from "../Swipeable/Swipeable";

import styles from "./dashboard-home.module.css";

const DashboardHome = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const appointments = useSelector((state: RootState) => state.appointments);
  const currentPage = useSelector((state: RootState) => state.tabs.dashboard);
  const user = useSelector((state: RootState) => state.user);

  const { fetchAppointments, loading } = useAppointment(
    {
      "filter[sinceDate]": moment().format("YYYY-MM-DD"),
      ...(user.roles?.includes(ROLES.TECHNICIAN) && {
        "filter[technicians.id]": user.userIdFromLogin,
      }),
    },
    true
  );
  const { handleAppointmentClicked } = useAppointmentClicked();

  const [hasOpen, setHasOpen] = useState(false);

  const checkOpenAppoitments = async () => {
    const response = await AppointmentService.HasOpen({
      ...(user.roles?.includes(ROLES.TECHNICIAN) && {
        "filter[technicians.id]": user.userIdFromLogin,
      }),
    });
    setHasOpen(response);
  };

  useEffect(() => {
    if (currentPage.url && currentPage.url !== "/") {
      navigate(currentPage.url);
    }
  }, [currentPage.url, navigate]);

  // Check if service is running and start clock
  useEffect(() => {
    dispatch(overviewAppointmentSliceActions.resetState());
    checkOpenAppoitments();

    UserService.GetCurrentUser()
      .then(({ data }) => {
        dispatch(userSliceActions.setCurrentUser(data));
        const userId = data?.user?.id;

        if (userId) {
          return TrackingEventService.GetAllByEmployee(userId);
        }

        dispatch(clockActions.setEmployeeId(userId));
      })
      .then(async (result: any) => {
        if (result.data[0]) {
          const appointmentId = result.data[0]?.appointmentId;
          const userId = result.data[0]?.employeeId;
          const isLastServiceStopped = result.data[0]?.eventType === "stop";

          if (!isLastServiceStopped) {
            dispatch(clockActions.setAppointmentId(appointmentId));

            const response = await AppointmentService.GetAppointment(
              appointmentId
            );
            dispatch(setAppointment(response.data));

            return calculateTotalTime(appointmentId, userId);
          }
        }

        return {
          totalTime: 0,
        };
      })
      .then((result) => {
        if (result.totalTime > 0) {
          // Set timer
          dispatch(clockActions.setIsRunning(true));
          dispatch(clockActions.setCurrentTime(result.totalTime));
          dispatch(clockActions.setInitialTime(result.totalTime));
        }
      })
      .catch(() => {
        localStorage.clear();
        dispatch(userSliceActions.resetState());
        navigate("/signin");
      });
  }, [dispatch, navigate]);

  const redirectToPastAppoitments = () => {
    navigate("/past-appointments");
  };

  return (
    <div className={styles["dashboard-home"]}>
      {loading ? (
        <LoadingSpinner loading={loading} />
      ) : (
        <>
          <div>
            {hasOpen && (
              <p
                className={styles["dashboard__past-appoitments"]}
                onClick={redirectToPastAppoitments}
              >
                {t("PastAppointmentsNotCompleted")}
              </p>
            )}
            <div className={styles["tasks-today__header"]}>
              <SubMenu showSearchButton={true}>
                {t("Today")}
                <span>({moment(new Date()).format("D.M.YYYY.")})</span>
              </SubMenu>
            </div>

            {/* Today's Appointments */}
            <div className={styles["tasks-today__list"]}>
              {appointments.today.length > 0 &&
                appointments.today.map((appointment) => (
                  <Swipeable
                    key={`today-${appointment.id}`}
                    appointment={appointment}
                    fetchAppointments={fetchAppointments}
                  >
                    <AppointmentCard
                      appointment={appointment}
                      dateInCircle={false}
                      upcoming={false}
                      onClick={() => handleAppointmentClicked(appointment)}
                      isDashboard
                    />
                  </Swipeable>
                ))}

              {appointments.today.length === 0 && (
                <NoData>{t("NoOpenOrders")} 🥳</NoData>
              )}
            </div>
          </div>

          {/* Tomorrow's Appointments */}
          {appointments.tomorrow.length > 0 && (
            <div className={styles["tasks-tomorrow"]}>
              <h3>
                {t("Morning")}{" "}
                <span>({moment().add(1, "days").format("DD.MM.YYYY.")})</span>
              </h3>

              <div className={styles["tasks-tomorrows__list"]}>
                {appointments.tomorrow.map((appointment) => (
                  <Swipeable
                    key={`today-${appointment.id}`}
                    appointment={appointment}
                    fetchAppointments={fetchAppointments}
                  >
                    <AppointmentCard
                      appointment={appointment}
                      dateInCircle={false}
                      upcoming={false}
                      onClick={() => handleAppointmentClicked(appointment)}
                      isDashboard
                    />
                  </Swipeable>
                ))}
              </div>
            </div>
          )}

          {appointments.future.length > 0 && (
            <div className={styles["tasks-upcoming"]}>
              <h3>{t("Upcoming")}</h3>

              <div className={styles["tasks-upcoming__list"]}>
                {appointments.future.map((appointment, i) => (
                  <Swipeable
                    key={i}
                    appointment={appointment}
                    fetchAppointments={fetchAppointments}
                  >
                    <AppointmentCard
                      appointment={appointment}
                      onClick={() => handleAppointmentClicked(appointment)}
                      isDashboard
                    />
                  </Swipeable>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DashboardHome;
